import { useEffect, useState } from "react";
import { Factory, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import type { InventoryRow } from "../types";
import { applyBulkStockEdits } from "../services/inventory";
import { Modal } from "./Modal";

interface Draft {
  variantId: string;
  galpao: number;
  malhariaEnabled: boolean;
  malharia: number;
}

export function BulkStockModal({ open, rows, onClose, onSaved }: { open: boolean; rows: InventoryRow[]; onClose: () => void; onSaved: () => void }) {
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [note, setNote] = useState("Inventário / edição em massa");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDrafts(rows.map((row) => ({
      variantId: row.variant.id,
      galpao: row.galpao.quantity,
      malhariaEnabled: row.variant.malhariaEnabled,
      malharia: row.malharia?.quantity ?? 0,
    })));
    setNote("Inventário / edição em massa");
  }, [open, rows]);

  const update = (variantId: string, changes: Partial<Draft>) => {
    setDrafts((current) => current.map((draft) => draft.variantId === variantId ? { ...draft, ...changes } : draft));
  };
  const close = () => { if (!saving) onClose(); };
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    try {
      const result = await applyBulkStockEdits(drafts, note);
      if (result.movements === 0 && result.variantsChanged === 0) toast.info("Nenhuma quantidade foi alterada.");
      else toast.success(`${result.movements} alteração(ões) registrada(s) no histórico.`);
      onSaved();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Não foi possível salvar a edição em massa.");
    } finally { setSaving(false); }
  };

  return <Modal open={open} onClose={close} title="Editar estoque em massa" description={`${rows.length} cor(es) selecionada(s). Cada valor alterado gera histórico e sincronização.`} wide>
    <form className="form-stack" onSubmit={submit}>
      <div className="bulk-stock-list">
        {rows.map((row) => {
          const draft = drafts.find((item) => item.variantId === row.variant.id);
          if (!draft) return null;
          return <article className="bulk-stock-row" key={row.variant.id}>
            <div className="bulk-stock-product">
              <i style={{ background: row.variant.colorCode }} />
              <div><code>{row.product.sku}</code><strong>{row.product.name}</strong><span>{row.variant.colorName}</span></div>
            </div>
            <label><span><PackageCheck size={15} />Galpão</span><input type="number" step="1" value={draft.galpao} onChange={(event) => update(row.variant.id, { galpao: Number(event.target.value) })} /></label>
            <label className="bulk-malharia-toggle"><span><Factory size={15} />Malharia</span><div><input type="checkbox" checked={draft.malhariaEnabled} onChange={(event) => update(row.variant.id, { malhariaEnabled: event.target.checked })} /><b>{draft.malhariaEnabled ? "Ativa" : "Desativada"}</b></div></label>
            <label><span>Quantidade na malharia</span><input type="number" step="1" value={draft.malharia} disabled={!draft.malhariaEnabled} onChange={(event) => update(row.variant.id, { malharia: Number(event.target.value) })} /></label>
          </article>;
        })}
      </div>
      <label><span>Motivo / observação</span><textarea rows={2} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Ex.: contagem física semanal" /></label>
      <div className="import-stock-note"><strong>Alteração segura</strong><span>Os valores são tratados como contagem final. Se outro aparelho alterar o mesmo saldo antes da sincronização, o sistema registra conflito em vez de sobrescrever silenciosamente.</span></div>
      <div className="modal-actions"><button type="button" className="ghost-button" disabled={saving} onClick={close}>Cancelar</button><button className="primary-button" disabled={saving || !drafts.length}>{saving ? "Salvando…" : "Salvar estoques selecionados"}</button></div>
    </form>
  </Modal>;
}
